import Link from 'next/link';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

import Button from 'components/Button';
import { cookies } from 'utils/constants';
import { destroyCookie } from 'nookies';
import config from 'utils/config';
import { AppContext } from 'pages/_app';
import { setCompanyData } from 'stores/actions';

import OnboardLogoWhite from 'public/images/landing/logo-white.svg';
import APIv2 from 'utils/api/v2';
import { useTranslation } from 'next-i18next';

type NavbarLandingProps = {
  hasLoginToken?: boolean /* hasLoginToken is true if user has login token in cookies */;
  type?: string;
};

const localeOptions = [
  { locale: 'en', flag: 'gb', longName: 'English' },
  { locale: 'id', flag: 'id', longName: 'Bahasa' },
];

export default function NavbarLanding({
  hasLoginToken,
  type,
}: NavbarLandingProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { dispatch } = useContext(AppContext);
  const [scrolled, setScrolled] = useState(false);
  const [sidebar, setSidebar] = useState(false);
  const [loggedInState, setLoggedInState] = useState<boolean>(null);

  const defaultMenus: Array<{
    link?: string;
    title: string | ReactElement | ((T: boolean) => void);
    isActive?: boolean;
  }> = [
    {
      title: 'Home',
      link: '/',
      isActive: pathname === '/',
    },
    {
      title: t('ForCompanies'),
      link: '/companies',
      isActive: pathname === '/companies',
    },
    {
      title: t('ForSeafarer'),
      link: '/seafarers',
      isActive: pathname === '/seafarers',
    },
    {
      title: t('About'),
      link: '/about',
      isActive: pathname === '/about',
    },
  ];

  function scrollListener() {
    const isScrolled = window.scrollY > window.innerHeight - 200;
    setScrolled(isScrolled);
  }

  const changeLocale = (locale: string) => {
    i18n.changeLanguage(locale);
    router.push(
      { pathname: router.pathname, query: router.query },
      router.asPath,
      { locale }
    );
  };

  const text = useMemo(() => {
    switch (loggedInState) {
      case true:
        return t('GoToDashboard');
      case false:
        return t('CompanySignIn');
      default:
        return t('CompanySignIn');
    }
  }, [
    hasLoginToken,
    loggedInState,
    i18n.language,
    router.pathname,
    defaultMenus,
  ]);

  const routes = useMemo(() => {
    switch (loggedInState) {
      case true:
        return '/dashboard';
      case false:
        return '/login';
      default:
        return '#';
    }
  }, [hasLoginToken, loggedInState]);

  const getTitleMobileMenu = useCallback((menu) => {
    if (!menu.title) return;
    if (typeof menu.title === 'function') {
      return menu.title();
    }

    return (
      <Link
        href={menu.link || '#'}
        className="text-primary font-medium text-large3"
        onClick={() => setSidebar(false)}
      >
        {menu.title}
      </Link>
    );
  }, []);

  const getTitleWebMenu = useCallback(
    (menu) => {
      if (!menu.title) return;
      if (typeof menu.title === 'function') {
        return menu.title(scrolled);
      }

      return (
        <Link href={menu.link || '#'} passHref className="text-white relative">
          <motion.div
            whileHover={{ opacity: 1, scale: 1.02 }}
            animate={{
              fontWeight: menu.isActive ? 600 : 300,
              opacity: menu.isActive ? 1 : 0.8,
              color: menu.isActive ? '#FFF' : '#FFF',
            }}
            transition={{ duration: 0.3 }}
            className="cursor-pointer"
          >
            {menu.title}
          </motion.div>
        </Link>
      );
    },
    [scrolled]
  );

  const menus = useMemo(() => {
    const tempMenus = [...defaultMenus];

    if (!tempMenus) return null;

    // language switcher

    // tempMenus.push({
    //   title: (scrolled) => (
    //     <FloatingMenu
    //       optionsClassName="w-max right-0"
    //       trigger={
    //         <div className="inline-flex items-center gap-1">
    //           <span
    //             className={clsx(
    //               'h-20 !w-28 flag-icon rounded !bg-cover border desktopSmall:border-none',
    //               `flag-icon-${localeOptions.find((item) => item.locale === i18n.language)
    //                 ?.flag
    //               }`
    //             )}
    //           />
    //           <span
    //             className={clsx(
    //               'ml-4 uppercase text-ink-lighter desktopSmall:text-white'
    //             )}
    //           >
    //             {i18n.language}
    //           </span>
    //           <i
    //             className={clsx(
    //               'ri-arrow-down-s-fill text-ink-lighter desktopSmall:text-white'
    //             )}
    //           />
    //         </div>
    //       }
    //       options={localeOptions.map((item) => ({
    //         value: (
    //           <div className="text-ink flex items-center gap-1">
    //             <span
    //               className={clsx(
    //                 'h-20 !w-28 flag-icon flex-none border rounded border-gray-200 !bg-cover',
    //                 `flag-icon-${item.flag}`
    //               )}
    //             />
    //             <span className='text-regular1 ml-4'>{item.longName}</span>
    //           </div>
    //         ),
    //         onClick: () => changeLocale(item.locale),
    //       }))}
    //     />
    //   ),
    // });

    tempMenus.push({
      title: (scrolled) => (
        <div>
          <Button
            text={text}
            size="small"
            className={`rounded bg-primary text-white desktopSmall:bg-white desktopSmall:text-primary hover:opacity-80`}
            onClick={() => router.push(routes, routes, { locale: '' })}
          />
        </div>
      ),
    });

    return tempMenus;
  }, [
    defaultMenus,
    text,
    routes,
    loggedInState,
    hasLoginToken,
    router.pathname,
    i18n.language,
    localeOptions,
  ]);

  useEffect(() => {
    document.addEventListener('scroll', scrollListener);

    return () => {
      document.removeEventListener('scroll', scrollListener);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebar ? 'hidden' : 'auto';
    if (sidebar) setScrolled(sidebar);
    else setScrolled(window.scrollY > (20 / 100) * document.body.clientHeight);
  }, [sidebar]);

  useEffect(() => {
    setSidebar(false);
  }, [router.asPath]);

  useEffect(() => {
    if (hasLoginToken) {
      APIv2.getCompanyV2({
        withCredentials: true,
      })
        .then((res) => {
          dispatch(setCompanyData(res.data));
          setLoggedInState(true);
        })
        .catch(() => {
          destroyCookie({}, cookies.token, { ...config.cookiesConfig });
          setLoggedInState(false);
        });
    } else {
      setLoggedInState(false);
    }
  }, [hasLoginToken]);

  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <>
      <header>
        <nav
          className={`fixed top-0 z-30 w-full transition-all ${
            scrolled || type === 'jobDetail'
              ? 'bg-green-dark/90 backdrop-blur-md shadow-xl h-48 desktopSmall:h-52'
              : 'bg-white/15 backdrop-blur-md h-48 desktopSmall:h-72'
          }`}
        >
          <div className="max-w-1280 w-full mx-auto px-20 desktopSmall:px-40 flex items-center justify-between h-full">
            <Link
              href="/"
              className="relative inline-block h-48 w-150 desktopSmall:h-64 desktopSmall:w-[170px]"
            >
              <Image
                src={OnboardLogoWhite}
                alt="OnBoard logo"
                fill
                style={{
                  objectFit: 'contain',
                }}
                priority
              />
            </Link>

            {menus && (
              <>
                <ul className="gap-x-32 px-16 text-white hidden desktopSmall:flex items-center">
                  {menus.slice(0, 4).map((menu, index) => (
                    <li key={index}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.2 }}
                        className="cursor-pointer"
                      >
                        {getTitleWebMenu(menu)}
                      </motion.div>
                    </li>
                  ))}
                </ul>
                <ul className="gap-x-32 text-white hidden desktopSmall:flex items-center">
                  {menus.slice(4, 6).map((menu, index) => (
                    <li key={index}>{getTitleWebMenu(menu)}</li>
                  ))}
                </ul>
                <span
                  onClick={() => setSidebar(!sidebar)}
                  className={`cursor-pointer text-large1 text-white rounded-md flex items-center justify-center desktopSmall:hidden w-32 h-32 relative -right-4`}
                >
                  <i
                    className={`${
                      sidebar
                        ? 'ri-close-large-line'
                        : 'ri-bar-chart-horizontal-line'
                    } text-subtitle3 rotate-180 text-green-lime`}
                  />
                </span>
              </>
            )}
          </div>
        </nav>
      </header>
      {menus && (
        <motion.nav
          className={`desktopSmall:hidden fixed inset-0 w-full h-screen shadow-md z-20 bg-white/90 backdrop-blur-sm pt-72 transition-transform right-0 transform desktop:translate-x-0 ${
            sidebar ? 'translate-x-0' : 'translate-x-full'
          }`}
          initial="hidden"
          animate={sidebar ? 'show' : 'hidden'}
          variants={containerVariants}
        >
          <motion.ul
            className="gap-x-24 px-24 text-black text-right flex flex-col h-full"
            variants={containerVariants}
          >
            {menus.map((menu, index) => (
              <motion.li className="py-12" key={index} variants={itemVariants}>
                {getTitleMobileMenu(menu)}
              </motion.li>
            ))}
          </motion.ul>
        </motion.nav>
      )}
    </>
  );
}
